import { z } from "zod";

const CancellationPenaltyBase = z.object({
    relativeTo: z.enum(["BOOKING_DATE", "TRAVEL_DATE"]),
    duration: z.string().nullable(),
});

export const CancellationPenaltyPercentage = CancellationPenaltyBase.extend({
    type: z.literal("PERCENTAGE"),
    refundPercentage: z.number().min(0).max(100),
});
export type CancellationPenaltyPercentage = z.infer<typeof CancellationPenaltyPercentage>;

export const CancellationPenaltyAbsolute = CancellationPenaltyBase.extend({
    type: z.literal("ABSOLUTE"),
    amount: z.number().min(0),
    amountCurrency: z.string().length(3),
    amountType: z.enum(["REFUND", "CHARGE"]),
});
export type CancellationPenaltyAbsolute = z.infer<typeof CancellationPenaltyAbsolute>;

export const CancellationPenalty = CancellationPenaltyPercentage.or(CancellationPenaltyAbsolute);
export type CancellationPenalty = z.infer<typeof CancellationPenalty>;

/*
 * PRODUCT CANCELLATION PENALTY
 * These are instances of Cancellation Penalty
 * Product cancellation penalty can be either static or dynamic
 */
const ProductCancellationPenaltyBase = z.object({
    id: z.string().nonempty(),
    productId: z.string().nonempty(),
    synchronisationKey: z.string().nullish(),
    synchronisationId: z.string().nullish(),
    synchronisedAt: z.coerce.date().nullish(),
    synchronisationDeletedAt: z.coerce.date().nullish(),
    createdAt: z.coerce.date().nullish(),
    updatedAt: z.coerce.date().nullish(),
});

export const ProductCancellationPenaltyPercentage = ProductCancellationPenaltyBase.merge(CancellationPenaltyPercentage);
export type ProductCancellationPenaltyPercentage = z.infer<typeof ProductCancellationPenaltyPercentage>;

export const ProductCancellationPenaltyAbsolute = ProductCancellationPenaltyBase.merge(CancellationPenaltyAbsolute);
export type ProductCancellationPenaltyAbsolute = z.infer<typeof ProductCancellationPenaltyAbsolute>;

export const ProductCancellationPenalty = ProductCancellationPenaltyPercentage.or(ProductCancellationPenaltyAbsolute);
export type ProductCancellationPenalty = z.infer<typeof ProductCancellationPenalty>;

// Other
export const ProductCancellationPenaltyInputDataSchema = z.record(z.string(), z.any());
export type ProductCancellationPenaltyInputData = z.infer<typeof ProductCancellationPenaltyInputDataSchema>;

export const ProductCancellationPenaltyDataStateSchema = z.enum(["FULL", "PARTIAL", "NONE"]);
export type ProductCancellationPenaltyDataState = z.infer<typeof ProductCancellationPenaltyDataStateSchema>;

export const Price = z.object({
    currency: z.string().length(3),
    gross: z.number().min(0),
    net: z.number().min(0),
});
export type Price = z.infer<typeof Price>;
export const CancellationPenaltyCalculationTypeSchema = z.enum(["PERCENTAGE", "ABSOLUTE"]);
export type CancellationPenaltyCalculationType = z.infer<typeof CancellationPenaltyCalculationTypeSchema>;

// Keep it here for now, until figure out package dependency graph: cyclic dependency detected
export const ProductPriceTypeSchema = z.enum(["PAID", "FREE"]);
export type ProductPriceType = z.infer<typeof ProductPriceTypeSchema>;

export const FULL_REFUND_POLICY = {
    type: "PERCENTAGE",
    relativeTo: "TRAVEL_DATE",
    duration: null,
    refundPercentage: 100,
} as const satisfies CancellationPenalty;

export const NO_REFUND_POLICY = {
    type: "PERCENTAGE",
    relativeTo: "TRAVEL_DATE",
    duration: null,
    refundPercentage: 0,
} as const satisfies CancellationPenalty;
